import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { CenteredShell, Headline, PageEyebrow, SplitShell } from '../components/auth-shells.js';
import { magToast } from '../components/toaster.js';
import { env } from '../lib/env.js';
import {
  consentLeg,
  describeClient,
  describeScope,
  type McpConsentContext,
} from '../lib/mcp-consent.js';
import { meQuery } from '../lib/queries.js';

// The OAuth consent screen for MCP clients (spec §13.5).
//
// Better Auth drives this page; it is reached in two different states and the
// difference is which query params arrive:
//
//   LOGIN LEG — the founder hit authorize while signed out. Better Auth
//   stashes the authorize query in a signed cookie and redirects here with
//   THE ORIGINAL AUTHORIZE QUERY (client_id, redirect_uri, scope,
//   code_challenge, state, resource...). Our job is to get them signed in and
//   then re-issue the same authorize request. Better Auth does not resume it
//   for us.
//
//   CONSENT LEG — they are signed in and this client needs consent. Better
//   Auth redirects here with consent_code + client_id + scope, having also
//   set a signed oidc_consent_prompt cookie. Our job is to show who is asking
//   and POST the verdict.
//
// WHY THIS PAGE HAS TO EXIST AT ALL: without a consentPage (or a
// getConsentHTML), better-auth's authorize throws 500 "No consent page
// provided" the moment consent is required — which is every new client. The
// flow does not merely look unfinished without this; it breaks.
//
// WHY IT IS PER-CLIENT, and not a one-time "allow MCP access" toggle: the MCP
// spec requires a provider with a static client id plus dynamic registration
// to obtain user consent for EACH dynamically registered client, or it is the
// confused deputy. Better Auth enforces that by keying consent on
// (client_id, user_id); this page is the surface for it.

const searchSchema = z.object({
  // Consent leg.
  consent_code: z.string().optional(),
  client_id: z.string().optional(),
  scope: z.string().optional(),
});

export const Route = createFileRoute('/mcp/authorize')({
  validateSearch: searchSchema,
  beforeLoad: async ({ context: { queryClient }, location }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) {
      // Come back to THIS url, query intact — on the login leg that query is
      // the entire authorize request, and dropping it would strand the client.
      throw redirect({
        to: '/login',
        search: { next: `${location.pathname}${location.searchStr}` },
      });
    }
  },
  component: McpAuthorizePage,
});

function McpAuthorizePage() {
  const search = Route.useSearch();
  // What this page is allowed to say, fetched from the server by consent code.
  //
  // NEVER rendered from the query string. The URL can be crafted: a link
  // carrying someone's pending consent_code plus a friendly client_id and a
  // harmless scope would display a reassuring screen while the click granted
  // whatever the server's record actually holds. The record is the only
  // truthful source, so the page waits for it and refuses to offer approval
  // without it.
  const [context, setContext] = useState<McpConsentContext | null>(null);
  const [contextFailed, setContextFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  // The redirect back to the client is a full page navigation to someone
  // else's origin, and it can take a visible moment. Without this the founder
  // watches a dead, still-rendered consent screen with a disabled button and
  // no indication their click landed. The destination is the client's, so
  // this frame is the last one we control — it says what is happening and who
  // they are being handed to.
  const [handingBack, setHandingBack] = useState(false);
  const { t } = useLingui();

  const isConsentLeg = consentLeg(search) === 'consent';

  // LOGIN LEG: signed in now (beforeLoad guaranteed it), so hand the original
  // authorize request back to the api and let better-auth continue.
  useEffect(() => {
    if (isConsentLeg) return;
    const qs = window.location.search;
    window.location.replace(`${env.API_URL}/api/auth/mcp/authorize${qs}`);
  }, [isConsentLeg]);

  useEffect(() => {
    if (!isConsentLeg || !search.consent_code) return;
    let cancelled = false;
    void fetch(
      `${env.API_URL}/api/mcp/consent-context?consent_code=${encodeURIComponent(search.consent_code)}`,
      { credentials: 'include' },
    )
      .then((res) => (res.ok ? (res.json() as Promise<McpConsentContext>) : null))
      .then((body) => {
        if (cancelled) return;
        if (body) setContext(body);
        else setContextFailed(true);
      })
      .catch(() => {
        if (!cancelled) setContextFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isConsentLeg, search.consent_code]);

  async function decide(accept: boolean) {
    setBusy(true);
    try {
      const res = await fetch(`${env.API_URL}/api/auth/oauth2/consent`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ accept, consent_code: search.consent_code }),
      });
      const body = (await res.json()) as { redirectURI?: string };
      if (!res.ok || !body.redirectURI) {
        throw new Error('consent failed');
      }
      // Swap to the handoff frame BEFORE navigating — the replace below can
      // take long enough to look broken, and this is the last frame we own.
      setHandingBack(true);
      // Back to the client, with the authorization code or an access_denied.
      window.location.replace(body.redirectURI);
    } catch {
      setBusy(false);
      setHandingBack(false);
      magToast.error('that did not go through. close this window and try connecting again.');
    }
  }

  // Every transactional state below uses the CENTERED shell and the consent
  // decision itself uses the SPLIT shell, which is the split auth-shells.tsx
  // already documents: split for committed moments, centered for mid-flow
  // ones. Handing an outside tool a key to your account is a committed
  // moment; waiting for a redirect is not.

  // Ordered ahead of the login-leg branch: once consent is granted this page
  // is mid-navigation to the client's origin, and the login-leg copy would
  // say the same thing less truthfully.
  if (handingBack) {
    return (
      <CenteredShell>
        <Headline>
          <Trans>that's done.</Trans>
        </Headline>
        <p className="text-[15px] text-[var(--ink)]/70">
          {context
            ? t`handing you back to ${describeClient(context.clientName, context.clientId)}…`
            : t`handing you back to your tool…`}
        </p>
      </CenteredShell>
    );
  }

  if (!isConsentLeg) {
    return (
      <CenteredShell>
        <p className="font-mono text-[11px] text-[var(--ink)]/60 uppercase tracking-[0.14em]">
          <Trans>taking you back to your tool…</Trans>
        </p>
      </CenteredShell>
    );
  }

  if (contextFailed) {
    return (
      <CenteredShell>
        <Headline>
          <Trans>this request has expired.</Trans>
        </Headline>
        <p className="text-[15px] text-[var(--ink)]/70 leading-relaxed">
          <Trans>close this window and connect again from your tool. nothing was approved.</Trans>
        </p>
      </CenteredShell>
    );
  }

  if (!context) {
    return (
      <CenteredShell>
        <p className="font-mono text-[11px] text-[var(--ink)]/60 uppercase tracking-[0.14em]">
          <Trans>checking what is being asked…</Trans>
        </p>
      </CenteredShell>
    );
  }

  // DEDUPED BY WHAT THEY SAY, not by scope name. `openid` and `profile` both
  // describe to "see your name", so a standard request rendered that line
  // twice and the screen looked broken on the one page that most needs to look
  // trustworthy.
  //
  // Collapsing identical SENTENCES hides nothing: two scopes with the same
  // description grant the same visible thing. The rule in mcp-consent.ts still
  // holds — an unrecognised scope is rendered verbatim, and a raw scope name is
  // unique, so it can never be absorbed into another line.
  const permissions = [...new Set(context.scopes.map(describeScope))];
  const asking = describeClient(context.clientName, context.clientId);

  return (
    // Mag's line is the reassurance the screen is actually making: the thing
    // a founder fears here is handing over the controls, and the answer is
    // that there are no controls on this surface to hand over.
    <SplitShell quote={t`they can read my work. they can't touch the controls.`}>
      <PageEyebrow>
        <Trans>connect a tool</Trans>
      </PageEyebrow>

      <div className="mt-3">
        <Headline>
          <Trans>connect {asking} to mag?</Trans>
        </Headline>
      </div>

      <p className="mt-3 text-[15px] text-[var(--ink)]/70 leading-relaxed">
        <Trans>
          it will be able to read your magig account from wherever you use it, ask me questions on
          your behalf, and ask you to approve a change to a campaign. it can never launch, pause, or
          spend on its own — you approve every change here, and i do the rest.
        </Trans>
      </p>

      {/* The grant itself, in the chunky card vocabulary the rest of the
          product uses for a connect surface. Boxed rather than inline
          because this list IS the decision — it should not read as a
          footnote under the headline. */}
      {permissions.length > 0 && (
        <ul className="mt-6 space-y-2 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)]">
          {permissions.map((permission) => (
            <li
              key={permission}
              className="flex items-baseline gap-2 text-[13.5px] text-[var(--ink)]/80"
            >
              <span aria-hidden="true" className="text-[var(--plum)]">
                ✦
              </span>
              {permission}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-5 text-[12.5px] text-[var(--ink)]/55 leading-relaxed">
        <Trans>
          you are approving this one tool. anything else you connect asks you separately.
        </Trans>
      </p>

      {/* Right-aligned actions, per the project's action-button rule. Deny
          keeps the quiet treatment and connect gets the chunky primary —
          the asymmetry is deliberate on a screen where the safe default is
          to walk away. */}
      <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-[var(--ink)]/8 pt-5">
        <button
          type="button"
          disabled={busy}
          onClick={() => void decide(false)}
          className="font-mono text-[10.5px] text-[var(--ink)]/55 uppercase tracking-[0.1em] hover:text-[var(--ink)] disabled:opacity-50"
        >
          <Trans>deny</Trans>
        </button>
        {/* The shared .btn, not a hand-rolled copy. It carries the motion every
            other primary button in the app has — a lift on hover
            (translate -2px, shadow out to 6px) and a press on active
            (translate +2px, shadow in to 1px). The hand-rolled version here
            transitioned only the shadow, so it neither lifted nor pressed and
            read as dead next to the rest of the product. */}
        <button
          type="button"
          disabled={busy}
          onClick={() => void decide(true)}
          className="btn disabled:opacity-60"
        >
          {busy ? <Trans>connecting…</Trans> : <Trans>connect</Trans>}
        </button>
      </div>
    </SplitShell>
  );
}
